import { Injectable } from '@angular/core';

/**
 * LEGACY WORKAROUND — Foxit PDF SDK for Web: font family dropdown is not sorted alphabetically.
 *
 * The Foxit SDK populates the font-family dropdown in the format panel in an arbitrary order
 * (registration order rather than alphabetical). There is no public API to control this ordering.
 *
 * Foxit support confirmed this is a known limitation and suggested the following workaround:
 * use a MutationObserver to detect when the dropdown element is added to the DOM and sort
 * its child list at that point. Because the format panel is durable for the life of the viewer
 * instance, the sort persists across panel close/reopen and document close/reopen cycles.
 *
 * NOTE: This service is expected to become unnecessary once Foxit ships the fix, likely in
 * version 11.1 or later based on feedback from their team.
 */
@Injectable({
    providedIn: 'root',
})
export class FoxitLegacyFontDropdownSorterService {
    public sortFontDropdownOnAppear(): void {
        const sortChildren = (container: Element): void => {
            const items = Array.from(container.children);
            if (items.length < 2) return;
            const sorted = [...items].sort((a, b) =>
                (a.textContent?.trim() ?? '').localeCompare(b.textContent?.trim() ?? ''),
            );
            if (sorted.some((item, i) => item !== items[i])) {
                sorted.forEach((item) => container.appendChild(item));
            }
        };

        const observer = new MutationObserver((mutations: MutationRecord[]) => {
            for (const mutation of mutations) {
                for (const node of Array.from(mutation.addedNodes)) {
                    if (!(node instanceof Element)) continue;

                    for (const candidate of Array.from(node.querySelectorAll('select, ul, ol'))) {
                        if (
                            candidate.parentElement?.parentElement?.innerHTML.includes(
                                'font-family-dropdown',
                            )
                        ) {
                            sortChildren(candidate);
                        }
                    }
                }
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });
    }
}
